import React from "react";
import {
  Brush,
  Palette,
  Wand2,
  Shirt,
  Archive,
  Printer,
  Images,
  Scissors,
  PartyPopper,
  Gift,
  Beaker,
  Gem,
  Package,
  Stamp,
  PaintBucket,
  Ribbon,
  Pin,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  parentId?: number | null;
  image?: any;
}

/* ──────────────────────────────────────────────
   ICON HELPER — keeps icon size consistent
   ────────────────────────────────────────────── */
const sz = 18; // default icon size (px)

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL ART & CRAFTS CATEGORIES (1200000xx)
   ────────────────────────────────────────────── */
export const idArtCraftsCategories: CategoryItem[] = [
  { id: 120000001, name: "Painting, Drawing & Art Supplies", icon: <Brush size={sz} /> },
  { id: 120000002, name: "Beading & Jewelry Making", icon: <Gem size={sz} /> },
  { id: 120000003, name: "Crafting", icon: <Wand2 size={sz} /> },
  { id: 120000004, name: "Fabric", icon: <Shirt size={sz} /> },
  { id: 120000005, name: "Fabric Decorating", icon: <PaintBucket size={sz} /> },
  { id: 120000006, name: "Knitting & Crochet", icon: <Pin size={sz} /> },
  { id: 120000007, name: "Needlework", icon: <Pin size={sz} /> },
  { id: 120000008, name: "Organization, Storage & Transport", icon: <Archive size={sz} /> },
  { id: 120000009, name: "Printmaking", icon: <Printer size={sz} /> },
  { id: 120000010, name: "Scrapbooking & Stamping", icon: <Images size={sz} /> },
  { id: 120000011, name: "Sewing", icon: <Scissors size={sz} /> },
  { id: 120000012, name: "Party Decorations & Supplies", icon: <PartyPopper size={sz} /> },
  { id: 120000013, name: "Gift Wrapping Supplies", icon: <Gift size={sz} /> },
];

/* ──────────────────────────────────────────────
   2️⃣ SUBCATEGORIES (1200001xx)
   ────────────────────────────────────────────── */
export const idArtCraftsSubcategories: CategoryItem[] = [
  /* Painting & Drawing */
  { id: 120000101, parentId: 120000001, name: "Paints & Brushes", icon: <Brush size={sz} /> },
  { id: 120000102, parentId: 120000001, name: "Sketching & Drawing Tools", icon: <Palette size={sz} /> },

  /* Beading & Jewelry */
  { id: 120000201, parentId: 120000002, name: "Beads & Charms", icon: <Gem size={sz} /> },
  { id: 120000202, parentId: 120000002, name: "Jewelry Tools & Wires", icon: <Beaker size={sz} /> },

  /* Crafting */
  { id: 120000301, parentId: 120000003, name: "DIY Craft Kits", icon: <Wand2 size={sz} /> },
  { id: 120000302, parentId: 120000003, name: "Craft Tools", icon: <Scissors size={sz} /> },

  /* Fabric */
  { id: 120000401, parentId: 120000004, name: "Cotton & Linen Fabrics", icon: <Shirt size={sz} /> },
  { id: 120000402, parentId: 120000004, name: "Specialty Fabrics", icon: <Shirt size={sz} /> },

  /* Fabric Decorating */
  { id: 120000501, parentId: 120000005, name: "Fabric Paints & Dyes", icon: <PaintBucket size={sz} /> },
  { id: 120000502, parentId: 120000005, name: "Iron-On Transfers", icon: <Images size={sz} /> },

  /* Knitting & Crochet */
  { id: 120000601, parentId: 120000006, name: "Yarns", icon: <Pin size={sz} /> },
  { id: 120000602, parentId: 120000006, name: "Hooks & Needles", icon: <Pin size={sz} /> },

  /* Needlework */
  { id: 120000701, parentId: 120000007, name: "Embroidery", icon: <Pin size={sz} /> },
  { id: 120000702, parentId: 120000007, name: "Cross Stitch", icon: <Pin size={sz} /> },

  /* Organization */
  { id: 120000801, parentId: 120000008, name: "Storage Boxes", icon: <Package size={sz} /> },
  { id: 120000802, parentId: 120000008, name: "Craft Organizers", icon: <Archive size={sz} /> },

  /* Printmaking */
  { id: 120000901, parentId: 120000009, name: "Screen Printing", icon: <Printer size={sz} /> },
  { id: 120000902, parentId: 120000009, name: "Block & Linocut Printing", icon: <Printer size={sz} /> },

  /* Scrapbooking */
  { id: 120001001, parentId: 120000010, name: "Albums & Papers", icon: <Images size={sz} /> },
  { id: 120001002, parentId: 120000010, name: "Stamps & Inks", icon: <Stamp size={sz} /> },

  /* Sewing */
  { id: 120001101, parentId: 120000011, name: "Sewing Machines", icon: <Scissors size={sz} /> },
  { id: 120001102, parentId: 120000011, name: "Threads & Accessories", icon: <Scissors size={sz} /> },

  /* Party Supplies */
  { id: 120001201, parentId: 120000012, name: "Balloons & Banners", icon: <PartyPopper size={sz} /> },
  { id: 120001202, parentId: 120000012, name: "Table Decor", icon: <PartyPopper size={sz} /> },

  /* Gift Wrapping */
  { id: 120001301, parentId: 120000013, name: "Wrapping Paper", icon: <Ribbon size={sz} /> },
  { id: 120001302, parentId: 120000013, name: "Gift Bags & Ribbons", icon: <Gift size={sz} /> },
];

/* ──────────────────────────────────────────────
   3️⃣ PRODUCTS (1200101xx+)
   ────────────────────────────────────────────── */
export const idArtCraftsProducts: CategoryItem[] = [
  /* Painting */
  { id: 120010101, parentId: 120000101, name: "Acrylic Paint Sets", icon: <Brush size={sz} /> },
  { id: 120010102, parentId: 120000101, name: "Watercolor Paints", icon: <Palette size={sz} /> },

  /* Jewelry */
  { id: 120020101, parentId: 120000201, name: "Glass Beads", icon: <Gem size={sz} /> },
  { id: 120020102, parentId: 120000201, name: "Metal Charms", icon: <Gem size={sz} /> },

  /* Knitting */
  { id: 120060101, parentId: 120000601, name: "Wool Yarns", icon: <Pin size={sz} /> },
  { id: 120060102, parentId: 120000601, name: "Cotton Yarns", icon: <Pin size={sz} /> },

  /* Sewing */
  { id: 120110201, parentId: 120001102, name: "Sewing Threads", icon: <Scissors size={sz} /> },
  { id: 120110202, parentId: 120001102, name: "Needles & Pins", icon: <Pin size={sz} /> },

  /* Gift Wrap */
  { id: 120130101, parentId: 120001301, name: "Printed Wrapping Paper", icon: <Ribbon size={sz} /> },
  { id: 120130201, parentId: 120001302, name: "Decorative Gift Bags", icon: <Gift size={sz} /> },
];